import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, FileBarChart2, ShieldCheck } from "lucide-react";
import { useEffect, useState } from "react";
import { DarkSectionBackdrop } from "@/components/DarkSectionBackdrop";
import { LightSectionBackdrop } from "@/components/LightSectionBackdrop";
import { SparkleDivider } from "@/components/SparkleDivider";

export const Route = createFileRoute("/tools/")({
  head: () => ({
    meta: [
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://scottcbusiness.com/tools" },
      { name: "twitter:title", content: "Free Content & Positioning Diagnostics | Scott Cunningham" },
      { name: "twitter:description", content: "Free diagnostics from Scott Cunningham (@scottcbusiness) to spot weak content systems and unclear trust signals before they cost you leads." },

      { title: "Free Content & Positioning Diagnostics | Scott Cunningham" },
      {
        name: "description",
        content:
          "Free diagnostics from Scott Cunningham (@scottcbusiness) to spot weak content systems and unclear trust signals before they cost you leads.",
      },
      { property: "og:title", content: "Free Content & Positioning Diagnostics" },
      {
        property: "og:description",
        content:
          "Free tools from Scott Cunningham (@scottcbusiness) that diagnose content systems and social profile trust signals for founders and creators.",
      },
    ],
    links: [{ rel: "canonical", href: "https://scottcbusiness.com/tools" }],
  }),
  component: ToolsHubPage,
});

function ToolsHubPage() {
  return (
    <div className="min-h-screen bg-background">
      <section className="relative isolate overflow-hidden bg-foreground text-cream">
        <DarkSectionBackdrop />
        <div className="relative mx-auto max-w-6xl px-6 py-20 sm:py-28">
          <span className="inline-flex items-center gap-2 rounded-full border border-cream/20 bg-cream/5 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-cream/80 backdrop-blur">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> Free diagnostics
          </span>
          <h1 className="font-display mt-6 text-left leading-[1.1] text-cream">
            <span className="block pb-3 text-[clamp(2.25rem,4.5vw,4rem)] [text-wrap:balance] min-[1600px]:whitespace-nowrap">
              Free <em className="not-italic text-gradient">Content &amp; Positioning</em>{" "}
              Diagnostics
            </span>
          </h1>
          <div className="mt-8 flex flex-col gap-5 sm:flex-row sm:items-center sm:gap-6">
            <p className="max-w-xl text-base text-cream/75 sm:text-lg">
              Two fast, no-fluff tools to help you spot what's working, what's quietly costing you
              reach, leads, and credibility, and what to fix next.
            </p>
            <div className="hidden w-px self-stretch bg-cream/15 sm:block" aria-hidden="true" />
            <div className="flex items-start gap-3">
              <span className="mt-1 text-xl text-rose-400" aria-hidden="true">
                ✦
              </span>
              <p className="text-sm font-semibold leading-snug text-cream sm:text-base">
                No signup. No sales pitch.
                <br />
                <span className="text-rose-400">Just clarity.</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      <SparkleDivider />

      <section className="relative isolate overflow-hidden">
        <LightSectionBackdrop variant="analytics" arcs={false} />
        <div className="relative mx-auto grid max-w-5xl gap-6 px-6 py-16">
          <ToolCard
            visual={<ScoreVisual />}
            icon={<FileBarChart2 className="h-7 w-7" />}
            title="Content System Readiness Score"
            description="Find out whether your content is planned, repeatable, measurable, or still stuck in reactive posting."
            cta="Check Your Content System"
            href="/tools/content-system-score"
            accent="orange"
          />
          <ToolCard
            visual={<TrustVisual />}
            icon={<ShieldCheck className="h-7 w-7" />}
            title="Social Profile Trust Scan"
            description="Check whether your LinkedIn, X, founder bio, or About section builds trust or just lists what you do."
            cta="Scan Your Profile"
            href="/tools/social-profile-scan"
            accent="purple"
          />
        </div>
      </section>

      <section className="relative isolate overflow-hidden">
        <LightSectionBackdrop variant="blueprint" arcs={false} sparkles={false} />
        <div className="mx-auto max-w-4xl px-6 pb-24">
          <div
            className="rounded-2xl border border-ink/15 bg-ink p-10 text-cream"
            style={{ boxShadow: "var(--shadow-soft)" }}
          >
            <h2 className="font-display text-3xl sm:text-4xl">
              A quick diagnostic is just the start.
            </h2>
            <p className="mt-3 max-w-2xl text-cream/80">
              These tools can help you spot the issue. The real value comes from fixing the system,
              improving the message, and building content that earns trust over time.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition hover:opacity-90"
              >
                Contact Scott <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/services"
                className="inline-flex items-center gap-2 rounded-full border border-cream/30 px-5 py-2.5 text-sm font-semibold text-cream transition hover:bg-cream/10"
              >
                View Services
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function ToolCard({
  icon,
  title,
  description,
  cta,
  href,
  visual,
  accent,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  cta: string;
  href: "/tools/content-system-score" | "/tools/social-profile-scan";
  visual?: React.ReactNode;
  accent: "orange" | "purple";
}) {
  const accentGradient =
    accent === "orange"
      ? "linear-gradient(90deg, #f97316, #ef4444)"
      : "linear-gradient(90deg, #a855f7, #6366f1)";
  const iconBg =
    accent === "orange"
      ? "bg-orange-100 text-orange-600 ring-orange-200"
      : "bg-violet-100 text-violet-600 ring-violet-200";
  const hoverGradient =
    accent === "orange"
      ? "linear-gradient(135deg, #f97316, #ef4444, #ec4899)"
      : "linear-gradient(135deg, #a855f7, #6366f1, #3b82f6)";
  const trustOnTop = accent === "purple";
  return (
    <Link
      to={href}
      className="group relative flex h-full flex-col overflow-hidden rounded-3xl border border-border bg-card p-7 transition hover:-translate-y-1 hover:shadow-xl sm:p-8"
      style={{ boxShadow: "var(--shadow-soft)" }}
    >
      {/* Animated gradient outline on hover */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 rounded-3xl opacity-0 transition-opacity duration-500 group-hover:opacity-100"
        style={{
          padding: "1.5px",
          background: hoverGradient,
          WebkitMask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
          WebkitMaskComposite: "xor",
          maskComposite: "exclude",
        }}
      />
      {trustOnTop ? (
        <div className="flex flex-col">
          <div className="flex items-center gap-5">
            <div
              className={`inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl ring-1 ${iconBg}`}
            >
              {icon}
            </div>
            {visual && <div className="min-w-0 flex-1">{visual}</div>}
          </div>
          <h2 className="font-display mt-6 text-3xl text-ink sm:text-4xl [text-wrap:balance]">
            {title}
          </h2>
          <div
            className="mt-3 h-[3px] w-20 rounded-full"
            style={{ background: accentGradient }}
            aria-hidden="true"
          />
          <p className="mt-5 text-base leading-relaxed text-ink/70">{description}</p>
          <div className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-ink transition-all group-hover:gap-3">
            {cta} <ArrowRight className="h-4 w-4" />
          </div>
        </div>
      ) : (
        <div className="grid h-full items-center gap-6 sm:grid-cols-[minmax(0,1.7fr)_minmax(0,1fr)] sm:gap-6">
          <div className="min-w-0 flex h-full flex-col justify-center self-center">
            <div
              className={`inline-flex h-14 w-14 items-center justify-center rounded-2xl ring-1 ${iconBg}`}
            >
              {icon}
            </div>
            <h2 className="font-display mt-5 text-3xl text-ink sm:text-[2rem] sm:leading-[1.1] lg:whitespace-nowrap">
              {title}
            </h2>
            <div
              className="mt-3 h-[3px] w-20 rounded-full"
              style={{ background: accentGradient }}
              aria-hidden="true"
            />
            <p className="mt-5 text-base leading-relaxed text-ink/70">{description}</p>
            <div className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-ink transition-all group-hover:gap-3">
              {cta} <ArrowRight className="h-4 w-4" />
            </div>
          </div>
          {visual && (
            <div className="flex h-full w-full items-center justify-center self-center">
              {visual}
            </div>
          )}
        </div>
      )}
    </Link>
  );
}

// ---------- Animated visuals ----------

const SCORE_STEPS = [
  { value: 30, color: "#ef4444", label: "Random" },
  { value: 60, color: "#f59e0b", label: "Reactive" },
  { value: 75, color: "#eab308", label: "Planned" },
  { value: 92, color: "#10b981", label: "Engine" },
];

function ScoreVisual() {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((p) => (p + 1) % SCORE_STEPS.length), 1800);
    return () => clearInterval(t);
  }, []);
  const step = SCORE_STEPS[i];
  const pct = step.value;
  const r = 105;
  const c = 2 * Math.PI * r;
  return (
    <div className="relative flex items-center justify-center">
      <svg width="280" height="280" viewBox="0 0 260 260" className="-rotate-90">
        <circle
          cx="130"
          cy="130"
          r={r}
          fill="none"
          stroke="currentColor"
          className="text-ink/10"
          strokeWidth="16"
        />
        <circle
          cx="130"
          cy="130"
          r={r}
          fill="none"
          stroke={step.color}
          strokeWidth="16"
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={c - (c * pct) / 100}
          style={{
            transition: "stroke-dashoffset 1.2s cubic-bezier(.6,.2,.2,1), stroke 1.2s ease",
          }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="flex items-baseline gap-0.5">
          <span
            className="font-display text-6xl tabular-nums"
            style={{ color: step.color, transition: "color 1.2s ease" }}
          >
            {pct}
          </span>
          <span className="text-lg text-ink/70">/100</span>
        </div>
        <span className="mt-1 text-xs font-semibold uppercase tracking-wider text-ink/75">
          {step.label}
        </span>
      </div>
    </div>
  );
}

const TRUST_STEPS = [42, 68, 84, 94];

function TrustVisual() {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((p) => (p + 1) % TRUST_STEPS.length), 1800);
    return () => clearInterval(t);
  }, []);
  const pct = TRUST_STEPS[i];
  return (
    <div className="w-full">
      <div className="min-w-0">
        <div className="flex items-baseline justify-between">
          <span className="text-xs font-semibold uppercase tracking-wider text-ink/75">
            Trust signal
          </span>
          <span className="font-display text-4xl tabular-nums bg-gradient-to-r from-rose-500 via-fuchsia-500 to-indigo-500 bg-clip-text text-transparent">
            {pct}%
          </span>
        </div>
        <div className="mt-3 h-4 w-full overflow-hidden rounded-full bg-ink/10">
          <div
            className="h-full rounded-full"
            style={{
              width: `${pct}%`,
              background: "linear-gradient(90deg, #ef4444, #ec4899, #a855f7, #6366f1, #3b82f6)",
              transition: "width 1.2s cubic-bezier(.6,.2,.2,1)",
            }}
          />
        </div>
      </div>
    </div>
  );
}
